import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BlockIcon from "@mui/icons-material/Block";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { getAdminApiErrorMessage } from "@/utils/adminApiErrors";
import { getChemistName } from "@/utils/textUtils";
import AppStatChip from "@/components/admin/AppStatChip";
import CheckInModal from "@/components/admin/CheckInModal";
import CheckInExclusionsModal from "@/components/admin/CheckInExclusionsModal";
import { APP_BRANDS } from "@/config/appBrands";
import { qudaAdminDataGridSx, mediDropDataGridSx } from "@/utils/adminTableStyles";

const NAVY = "#002169";
const RED = "#b91c1c";
const GREEN = "#166534";

const QUDA = APP_BRANDS.quda;
const MEDIDROP = APP_BRANDS.medidrop;

// ── Mirrors PHAPPSV1/routes/ManagerV1/phAdminV1/CheckInsV1/classification.js ──
// Kept in sync manually — pure threshold helpers, no DB access.
function trialPcsTier(n) {
  if (n <= 1) return "red";
  if (n <= 3) return "lightRed";
  if (n <= 4) return "yellow";
  return "green";
}
function trialStaffTier(n) {
  if (n === 0) return "red";
  if (n <= 2) return "lightRed";
  if (n <= 4) return "yellow";
  return "green";
}
function trialOrdersTier(n) {
  if (n === 0) return "red";
  if (n < 5) return "lightRed";
  if (n < 15) return "yellow";
  return "green";
}
function trialOrdersPerDayTier(n) {
  if (n <= 0) return "red";
  if (n < 0.5) return "lightRed";
  if (n < 1) return "yellow";
  return "green";
}
function trialSmsTier(n) {
  if (n === 0) return "red";
  if (n < 5) return "lightRed";
  if (n < 15) return "yellow";
  return "green";
}
function trialSmsPerDayTier(n) {
  if (n <= 0) return "red";
  if (n < 0.5) return "lightRed";
  if (n < 1) return "yellow";
  return "green";
}
function trialMediDropDeliveryTier(n) {
  if (n === 0) return "red";
  if (n < 3) return "lightRed";
  if (n < 8) return "yellow";
  return "green";
}
function trialMediDropDriversTier(n) {
  if (n === 0) return "red";
  if (n === 1) return "lightRed";
  if (n <= 2) return "yellow";
  return "green";
}

const TIER_COLORS = {
  red:      { bg: "#fee2e2", fg: "#991b1b" },
  lightRed: { bg: "#fef2f2", fg: "#b91c1c" },
  yellow:   { bg: "#fef9c3", fg: "#854d0e" },
  green:    { bg: "#dcfce7", fg: "#166534" },
};

function TierCell({ value, tier }) {
  const c = TIER_COLORS[tier] || TIER_COLORS.yellow;
  return (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
      <Box
        sx={{
          backgroundColor: c.bg,
          color: c.fg,
          fontWeight: 700,
          fontSize: "0.8rem",
          borderRadius: "6px",
          px: 1,
          py: 0.25,
          minWidth: 36,
          textAlign: "center",
        }}
      >
        {value}
      </Box>
    </Box>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const p = String(iso).split("T")[0].split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0].slice(2)}` : iso;
}

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * A pharmacy is due for a check-in unless it's been retired ("stop check-ins") or
 * its re-check date is still in the future. No schedule at all = due, because
 * nobody has decided when to call them back.
 */
const isDue = (row, today) => {
  if (row.stopCheckIns) return false;
  if (!row.nextCheckInDate) return true;
  return String(row.nextCheckInDate).slice(0, 10) <= today;
};

/** Whole days from today to a YYYY-MM-DD date (negative = in the past). */
const daysUntil = (ymdStr) => {
  const target = new Date(`${String(ymdStr).slice(0, 10)}T00:00:00`);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.round((target - now) / (1000 * 60 * 60 * 24));
};

const CheckInsPage = () => {
  const navigate = useNavigate();
  const chemistBasePath = "/admin-functions/chemist";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [tab, setTab] = useState(0);
  // Worklists show only what's actually due. Tick to reveal pharmacies that are
  // scheduled for a later date or have been retired from check-ins.
  const [showScheduled, setShowScheduled] = useState(false);
  // Pharmacy-name search — filters the current tab's rows by name.
  const [search, setSearch] = useState("");
  // Row currently open in the check-in modal.
  const [checkInRow, setCheckInRow] = useState(null);
  // Ignored-pharmacies modal. `presetChemist` is set when opened from a row's hide
  // action so the add form starts pre-filled; null when opened from the toolbar.
  const [exclusionsOpen, setExclusionsOpen] = useState(false);
  const [exclusionPreset, setExclusionPreset] = useState(null);

  // MediDrop Trials tab — lazy loaded on first visit to tab index 3.
  const [mediDropTrialData, setMediDropTrialData] = useState(null);
  const [mediDropTrialLoading, setMediDropTrialLoading] = useState(false);
  const [mediDropTrialError, setMediDropTrialError] = useState(null);
  const mediDropFetched = useRef(false);

  const today = todayYmd();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await phappsRequest(API_PATHS.CHECK_INS_OVERVIEW, "GET");
      setData(res);
      setFetchError(null);
    } catch (e) {
      setData(null);
      setFetchError(getAdminApiErrorMessage(e, "Failed to load Check-Ins data"));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMediDropTrials = useCallback(async ({ force = false } = {}) => {
    if (mediDropFetched.current && !force) return;
    mediDropFetched.current = true;
    try {
      setMediDropTrialLoading(true);
      const res = await phappsRequest(API_PATHS.ADMIN_MEDIDROP_TRIALS, "GET");
      setMediDropTrialData(res);
      setMediDropTrialError(null);
    } catch (e) {
      setMediDropTrialError(getAdminApiErrorMessage(e, "Failed to load MediDrop Trials data"));
    } finally {
      setMediDropTrialLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadMediDropTrials();
  }, [load, loadMediDropTrials]);

  const handleTabChange = (_, v) => {
    setTab(v);
    setSearch("");
    if (v === 3) loadMediDropTrials();
  };

  const goToTab = (v) => {
    setTab(v);
    setSearch("");
    if (v === 3) loadMediDropTrials();
  };

  // A check-in changes both a pharmacy's last-check-in date and its schedule, so
  // pull both tables again rather than patching rows locally.
  const handleCheckInSaved = useCallback(() => {
    load();
    loadMediDropTrials({ force: true });
  }, [load, loadMediDropTrials]);

  const summary = data?.SummaryCards || {};
  const qudaSubscriptions = Number(summary.QudaSubscriptions ?? 0);
  const atRiskPct = Number(summary.AtRisk?.pctOfTotal ?? 0);

  // ── Shared cell renderers ──
  const chemistNameColumn = {
    field: "chemistName",
    headerName: "Chemist",
    flex: 1,
    minWidth: 190,
    renderCell: (p) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, height: "100%", width: "100%" }}>
        {p.row.extremelyAtRisk && (
          <Tooltip arrow title="Extremely at-risk — stayed At-Risk for 30+ days despite a check-in">
            <WarningAmberOutlinedIcon sx={{ color: RED, fontSize: 18, flexShrink: 0 }} />
          </Tooltip>
        )}
        <Typography
          variant="body2"
          onClick={() =>
            p.row.chemistUUID &&
            navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`)
          }
          sx={{
            cursor: p.row.chemistUUID ? "pointer" : "default",
            fontWeight: 500,
            "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" },
          }}
        >
          {getChemistName(p.value)}
        </Typography>
      </Box>
    ),
  };

  const dateCell = (p) => (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
      <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#374151" }}>
        {fmtDate(p.value)}
      </Typography>
    </Box>
  );

  /**
   * Cross-app usage cell (MediDrop deliveries / DocLink consults). Non-zero values are
   * bold in the owning app's colour — on the At-Risk tab that's the tell that a
   * pharmacy flagged for zero Quda activity is actually alive on another app.
   */
  const usageCell = (app) => (p) => {
    const n = Number(p.value ?? 0);
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: "0.82rem",
            fontWeight: n > 0 ? 700 : 400,
            color: n > 0 ? APP_BRANDS[app].main : "#9ca3af",
          }}
        >
          {n.toLocaleString()}
        </Typography>
      </Box>
    );
  };

  const deliveriesColumn = {
    field: "deliveries30d",
    headerName: "Deliveries (30d)",
    width: 135,
    type: "number",
    renderCell: usageCell("medidrop"),
  };

  const consultsColumn = {
    field: "consults30d",
    headerName: "Consults (30d)",
    width: 130,
    type: "number",
    renderCell: usageCell("doclink"),
  };

  // Last-check-in cell: date, plus a small "✎ manual" marker when the latest check-in
  // was logged manually (outside Dialpad).
  const lastCheckInCell = (p) => (
    <Box sx={{ display: "flex", alignItems: "center", height: "100%", gap: 0.5 }}>
      <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#374151" }}>
        {fmtDate(p.value)}
      </Typography>
      {p.row.lastCheckInSource === "manual" && (
        <Tooltip arrow title="The latest check-in was logged manually (outside Dialpad).">
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              fontSize: "0.66rem",
              fontWeight: 700,
              color: NAVY,
              backgroundColor: "#eef2ff",
              border: "1px solid #dfe4ff",
              borderRadius: "4px",
              px: 0.5,
              lineHeight: 1.5,
            }}
          >
            ✎ manual
          </Box>
        </Tooltip>
      )}
    </Box>
  );

  // Next-check-in cell: the scheduled re-check, or why there isn't one.
  const nextCheckInCell = (p) => {
    if (p.row.stopCheckIns) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Tooltip arrow title="Check-ins stopped — don't call this pharmacy again.">
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "#6b7280",
                backgroundColor: "#f3f4f6",
                border: "1px solid #e5e7eb",
                borderRadius: "6px",
                px: 0.75,
                py: 0.25,
              }}
            >
              <BlockIcon sx={{ fontSize: 13 }} />
              Stopped
            </Box>
          </Tooltip>
        </Box>
      );
    }
    if (!p.value) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography variant="body2" sx={{ fontSize: "0.8rem", fontWeight: 700, color: RED }}>
            Due now
          </Typography>
        </Box>
      );
    }
    const d = daysUntil(p.value);
    const overdue = d <= 0;
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, height: "100%" }}>
        <Typography
          variant="body2"
          sx={{ fontSize: "0.82rem", fontWeight: overdue ? 700 : 500, color: overdue ? RED : "#374151" }}
        >
          {fmtDate(p.value)}
        </Typography>
        <Typography variant="caption" sx={{ color: overdue ? RED : "#9ca3af" }}>
          {overdue ? (d === 0 ? "(today)" : `(${Math.abs(d)}d overdue)`) : `(${d}d)`}
        </Typography>
      </Box>
    );
  };

  const checkInActionColumn = (app) => ({
    field: "_checkIn",
    headerName: "",
    width: 142,
    sortable: false,
    filterable: false,
    renderCell: (p) => (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, height: "100%" }}>
        <Box
          component="button"
          onClick={(e) => {
            e.stopPropagation();
            setCheckInRow({ ...p.row, app });
          }}
          disabled={!p.row.chemistUUID}
          sx={{
            px: 1.1,
            py: 0.4,
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            background: APP_BRANDS[app].main,
            cursor: p.row.chemistUUID ? "pointer" : "default",
            opacity: p.row.chemistUUID ? 1 : 0.4,
            "&:hover": { background: APP_BRANDS[app].dark },
          }}
        >
          Check in
        </Box>
        <Tooltip arrow title="Ignore this pharmacy — hide it from Check-Ins entirely (test / internal account)">
          <Box
            component="button"
            onClick={(e) => {
              e.stopPropagation();
              setExclusionPreset({ chemistUUID: p.row.chemistUUID, chemistName: p.row.chemistName });
              setExclusionsOpen(true);
            }}
            disabled={!p.row.chemistUUID}
            aria-label="Ignore this pharmacy"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              background: "#fff",
              color: "#6b7280",
              cursor: p.row.chemistUUID ? "pointer" : "default",
              opacity: p.row.chemistUUID ? 1 : 0.4,
              "&:hover": { background: "#f3f4f6", color: "#374151" },
            }}
          >
            <VisibilityOffOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
        </Tooltip>
      </Box>
    ),
  });

  // ── At-Risk table ──
  const atRiskRows = useMemo(
    () =>
      (data?.AtRiskTable || []).map((r, i) => ({
        id: r.ChemistUUID || `ar-${i}`,
        chemistUUID: r.ChemistUUID,
        chemistName: r.ChemistName,
        installationDate: r.InstallationDate,
        lastOrderDate: r.LastOrderDate,
        lastSmsDate: r.LastSmsDate,
        totalOrders: r.TotalOrders ?? 0,
        totalSms: r.TotalSms ?? 0,
        deliveries30d: r.Deliveries30d ?? 0,
        consults30d: r.Consults30d ?? 0,
        lastCheckInDate: r.LastCheckInDate,
        lastCheckInSource: r.LastCheckInSource,
        nextCheckInDate: r.NextCheckInDate ?? null,
        stopCheckIns: !!r.StopCheckIns,
        extremelyAtRisk: !!r.ExtremelyAtRisk,
      })),
    [data]
  );

  const atRiskColumns = [
    chemistNameColumn,
    { field: "installationDate", headerName: "Installed", width: 105, renderCell: dateCell },
    { field: "lastOrderDate", headerName: "Last order", width: 110, renderCell: dateCell },
    { field: "lastSmsDate", headerName: "Last SMS", width: 110, renderCell: dateCell },
    { field: "totalOrders", headerName: "Total orders", width: 110, type: "number" },
    { field: "totalSms", headerName: "Total SMS", width: 105, type: "number" },
    deliveriesColumn,
    consultsColumn,
    { field: "lastCheckInDate", headerName: "Last check-in", width: 150, renderCell: lastCheckInCell },
    { field: "nextCheckInDate", headerName: "Next check-in", width: 165, renderCell: nextCheckInCell },
    checkInActionColumn("quda"),
  ];

  // ── Excellent table ──
  const excellentRows = useMemo(
    () =>
      (data?.ExcellentTable || []).map((r, i) => ({
        id: r.ChemistUUID || `ex-${i}`,
        chemistUUID: r.ChemistUUID,
        chemistName: r.ChemistName,
        installationDate: r.InstallationDate,
        orders30d: r.Orders30d ?? 0,
        ordersPerDay30d: r.OrdersPerDay30d ?? 0,
        sms30d: r.Sms30d ?? 0,
        smsPerDay30d: r.SmsPerDay30d ?? 0,
        deliveries30d: r.Deliveries30d ?? 0,
        consults30d: r.Consults30d ?? 0,
        lastCheckInDate: r.LastCheckInDate,
        lastCheckInSource: r.LastCheckInSource,
        nextCheckInDate: r.NextCheckInDate ?? null,
        stopCheckIns: !!r.StopCheckIns,
      })),
    [data]
  );

  const excellentColumns = [
    chemistNameColumn,
    { field: "installationDate", headerName: "Installed", width: 105, renderCell: dateCell },
    { field: "orders30d", headerName: "Orders (30d)", width: 115, type: "number" },
    { field: "ordersPerDay30d", headerName: "Orders / day", width: 115, type: "number" },
    { field: "sms30d", headerName: "SMS (30d)", width: 105, type: "number" },
    { field: "smsPerDay30d", headerName: "SMS / day", width: 105, type: "number" },
    deliveriesColumn,
    consultsColumn,
    { field: "lastCheckInDate", headerName: "Last check-in", width: 150, renderCell: lastCheckInCell },
    { field: "nextCheckInDate", headerName: "Next check-in", width: 165, renderCell: nextCheckInCell },
    checkInActionColumn("quda"),
  ];

  // ── Quda Trials table ──
  const trialRows = useMemo(
    () =>
      (data?.TrialTable || []).map((r, i) => ({
        id: r.ChemistUUID || `tr-${i}`,
        chemistUUID: r.ChemistUUID,
        chemistName: r.ChemistName,
        installationDate: r.InstallationDate,
        pcs: r.Pcs ?? 0,
        staff: r.Staff ?? 0,
        orders30d: r.Orders30d ?? 0,
        ordersPerDay30d: r.OrdersPerDay30d ?? 0,
        sms30d: r.Sms30d ?? 0,
        smsPerDay30d: r.SmsPerDay30d ?? 0,
        deliveries30d: r.Deliveries30d ?? 0,
        consults30d: r.Consults30d ?? 0,
        trialDaysLeft: r.TrialDaysLeft,
        lastCheckInDate: r.LastCheckInDate,
        lastCheckInSource: r.LastCheckInSource,
        nextCheckInDate: r.NextCheckInDate ?? null,
        stopCheckIns: !!r.StopCheckIns,
      })),
    [data]
  );

  const trialDaysLeftColumn = {
    field: "trialDaysLeft",
    headerName: "Trial days left",
    width: 120,
    type: "number",
    renderCell: (p) => (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, color: p.value != null && p.value <= 3 && p.value >= 0 ? RED : "#374151" }}
        >
          {p.value == null || p.value < 0 ? "—" : p.value}
        </Typography>
      </Box>
    ),
  };

  const trialColumns = [
    chemistNameColumn,
    { field: "installationDate", headerName: "Installed", width: 105, renderCell: dateCell },
    trialDaysLeftColumn,
    { field: "pcs", headerName: "PCs", width: 85, renderCell: (p) => <TierCell value={p.value} tier={trialPcsTier(p.value)} /> },
    { field: "staff", headerName: "Staff", width: 85, renderCell: (p) => <TierCell value={p.value} tier={trialStaffTier(p.value)} /> },
    { field: "orders30d", headerName: "Orders (30d)", width: 115, renderCell: (p) => <TierCell value={p.value} tier={trialOrdersTier(p.value)} /> },
    { field: "ordersPerDay30d", headerName: "Orders / day", width: 115, renderCell: (p) => <TierCell value={p.value} tier={trialOrdersPerDayTier(p.value)} /> },
    { field: "sms30d", headerName: "SMS (30d)", width: 105, renderCell: (p) => <TierCell value={p.value} tier={trialSmsTier(p.value)} /> },
    { field: "smsPerDay30d", headerName: "SMS / day", width: 105, renderCell: (p) => <TierCell value={p.value} tier={trialSmsPerDayTier(p.value)} /> },
    deliveriesColumn,
    consultsColumn,
    { field: "lastCheckInDate", headerName: "Last check-in", width: 150, renderCell: lastCheckInCell },
    checkInActionColumn("quda"),
  ];

  // ── MediDrop Trials table ──
  const mediDropTrialRows = useMemo(
    () =>
      (mediDropTrialData?.MediDropTrialTable || []).map((r, i) => ({
        id: r.ChemistUUID || `md-${i}`,
        chemistUUID: r.ChemistUUID,
        chemistName: r.ChemistName,
        pcs: r.Pcs ?? 0,
        staff: r.Staff ?? 0,
        deliveries: r.Deliveries ?? 0,
        consults30d: r.Consults30d ?? 0,
        scheduledDeliveries: r.ScheduledDeliveries ?? 0,
        latestDelivery: r.LatestDelivery,
        trialDaysLeft: r.TrialDaysLeft,
        lastCheckInDate: r.LastCheckInDate,
        lastCheckInSource: r.LastCheckInSource,
        nextCheckInDate: r.NextCheckInDate ?? null,
        stopCheckIns: !!r.StopCheckIns,
      })),
    [mediDropTrialData]
  );

  const mediDropTrialColumns = [
    {
      field: "chemistName",
      headerName: "Chemist",
      flex: 1,
      minWidth: 190,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", width: "100%" }}>
          <Typography
            variant="body2"
            onClick={() =>
              p.row.chemistUUID &&
              navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, {
                state: { tab: 5 },
              })
            }
            sx={{
              cursor: p.row.chemistUUID ? "pointer" : "default",
              fontWeight: 500,
              "&:hover": { textDecoration: p.row.chemistUUID ? "underline" : "none" },
            }}
          >
            {getChemistName(p.value)}
          </Typography>
        </Box>
      ),
    },
    { field: "pcs", headerName: "PCs", width: 80, renderCell: (p) => <TierCell value={p.value} tier={trialPcsTier(p.value)} /> },
    { field: "staff", headerName: "Staff (Drivers)", width: 130, renderCell: (p) => <TierCell value={p.value} tier={trialMediDropDriversTier(p.value)} /> },
    { field: "deliveries", headerName: "Deliveries (30d)", width: 140, renderCell: (p) => <TierCell value={p.value} tier={trialMediDropDeliveryTier(p.value)} /> },
    consultsColumn,
    { field: "scheduledDeliveries", headerName: "Scheduled", width: 105, type: "number" },
    { field: "latestDelivery", headerName: "Latest delivery", width: 125, renderCell: dateCell },
    trialDaysLeftColumn,
    { field: "lastCheckInDate", headerName: "Last check-in", width: 150, renderCell: lastCheckInCell },
    checkInActionColumn("medidrop"),
    {
      field: "_open",
      headerName: "",
      width: 88,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Box
            component="button"
            onClick={() =>
              p.row.chemistUUID &&
              navigate(`${chemistBasePath}/${encodeURIComponent(p.row.chemistUUID)}`, {
                state: { tab: 5 },
              })
            }
            disabled={!p.row.chemistUUID}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              px: 1,
              py: 0.4,
              fontSize: "0.75rem",
              fontWeight: 600,
              color: MEDIDROP.dark,
              border: `1px solid ${MEDIDROP.main}`,
              borderRadius: "6px",
              background: "#fff",
              cursor: p.row.chemistUUID ? "pointer" : "default",
              opacity: p.row.chemistUUID ? 1 : 0.4,
              "&:hover": { background: MEDIDROP.tint },
            }}
          >
            <OpenInNewIcon sx={{ fontSize: 13 }} />
            Open
          </Box>
        </Box>
      ),
    },
  ];

  // Worklist tabs (At-Risk / Excellent) drain as pharmacies get scheduled; trial tabs
  // don't, because trials fall off the list by themselves when the trial ends.
  const tables = useMemo(
    () => [
      {
        key: "atRisk",
        label: "At-Risk",
        app: "quda",
        rows: atRiskRows,
        columns: atRiskColumns,
        worklist: true,
        caption:
          "Active Quda accounts with zero orders and zero SMS in the last 30 days, sorted by total SMS (heaviest past users first). Rows flagged ⚠ have stayed At-Risk for 30+ days despite a check-in.",
      },
      {
        key: "excellent",
        label: "Excellent",
        app: "quda",
        rows: excellentRows,
        columns: excellentColumns,
        worklist: true,
        caption: "Active Quda accounts averaging more than 10 orders/day and 10 SMS/day over the last 30 days.",
      },
      {
        key: "qudaTrials",
        label: "Quda Trials",
        app: "quda",
        rows: trialRows,
        columns: trialColumns,
        worklist: false,
        caption: "Chemists currently in their Quda trial period, sorted by days remaining. Trials leave this list when the trial ends.",
      },
      {
        key: "mediDropTrials",
        label: "MediDrop Trials",
        app: "medidrop",
        rows: mediDropTrialRows,
        columns: mediDropTrialColumns,
        worklist: false,
        caption:
          "Pharmacies currently trialling MediDrop. Staff = registered drivers. Deliveries = last 30 days (excluding cancelled/draft). Open → navigates to that pharmacy's MediDrop tab.",
      },
    ],
    [atRiskRows, excellentRows, trialRows, mediDropTrialRows]
  );

  const current = tables[tab];
  const accent = APP_BRANDS[current.app].main;

  // Due counts drive the headline numbers — the worklists are meant to empty out,
  // so "12 of 60" is the useful reading, not "60".
  const atRiskDue = useMemo(() => atRiskRows.filter((r) => isDue(r, today)).length, [atRiskRows, today]);
  const excellentDue = useMemo(() => excellentRows.filter((r) => isDue(r, today)).length, [excellentRows, today]);

  const displayRows = useMemo(() => {
    let rows = current.rows;
    if (current.worklist && !showScheduled) {
      rows = rows.filter((r) => isDue(r, today));
    }
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => getChemistName(r.chemistName).toLowerCase().includes(q));
    }
    return rows;
  }, [current, showScheduled, today, search]);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <div className="p-4 space-y-3 w-full max-w-full">
        <Typography variant="h5" fontWeight="bold" sx={{ color: NAVY }}>
          Check-Ins — Account Health
        </Typography>

        {fetchError ? <Alert severity="warning">{fetchError}</Alert> : null}

        {loading && !data ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div
                className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4"
                style={{ borderColor: NAVY }}
              />
              <p className="text-gray-600">Loading Check-Ins data…</p>
            </div>
          </div>
        ) : (
          <>
            {/* ── Compact, app-themed summary strip: subscriptions on the left, trials in
                   their own section on the right. Clicking a tile selects its tab. ── */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
              <Box sx={{ flex: "3 1 460px", minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#9ca3af", mb: 0.5 }}
                >
                  Subscriptions
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <AppStatChip
                    app="quda"
                    label="Quda subs"
                    value={qudaSubscriptions.toLocaleString()}
                  />
                  <AppStatChip
                    color={RED}
                    Icon={WarningAmberOutlinedIcon}
                    label="At-Risk due"
                    value={atRiskDue.toLocaleString()}
                    caption={`${atRiskRows.length} at-risk · ${atRiskPct}%`}
                    active={tab === 0}
                    onClick={() => goToTab(0)}
                  />
                  <AppStatChip
                    color={GREEN}
                    Icon={EmojiEventsOutlinedIcon}
                    label="Excellent due"
                    value={excellentDue.toLocaleString()}
                    caption={`${excellentRows.length} excellent`}
                    active={tab === 1}
                    onClick={() => goToTab(1)}
                  />
                </Box>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ display: { xs: "none", lg: "block" } }} />

              <Box sx={{ flex: "2 1 320px", minWidth: 0 }}>
                <Typography
                  sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#9ca3af", mb: 0.5 }}
                >
                  Trials
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <AppStatChip
                    app="quda"
                    label="Quda"
                    value={trialRows.length.toLocaleString()}
                    active={tab === 2}
                    onClick={() => goToTab(2)}
                  />
                  <AppStatChip
                    app="medidrop"
                    label="MediDrop"
                    value={mediDropTrialLoading ? "…" : mediDropTrialRows.length.toLocaleString()}
                    active={tab === 3}
                    onClick={() => goToTab(3)}
                  />
                </Box>
              </Box>
            </Box>

            <Card variant="outlined" sx={{ minWidth: 0, borderColor: accent, borderRadius: 2, overflow: "hidden" }}>
              <CardContent sx={{ p: 0 }}>
                <Tabs
                  value={tab}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    px: 2,
                    pt: 0.5,
                    minHeight: 42,
                    "& .MuiTab-root": { minHeight: 42, fontWeight: 600, textTransform: "none", gap: 0.75 },
                    "& .Mui-selected": { color: `${accent} !important` },
                    "& .MuiTabs-indicator": { backgroundColor: accent, height: 3 },
                  }}
                >
                  {tables.map((t, i) => {
                    const brand = APP_BRANDS[t.app];
                    return (
                      <Tab
                        key={t.key}
                        iconPosition="start"
                        icon={
                          brand.icon ? (
                            <Box
                              component="img"
                              src={brand.icon}
                              alt=""
                              sx={{
                                width: 18,
                                height: 18,
                                objectFit: "contain",
                                opacity: tab === i ? 1 : 0.55,
                              }}
                            />
                          ) : undefined
                        }
                        label={`${t.label} (${t.rows.length})`}
                      />
                    );
                  })}
                </Tabs>
                <Divider />
                <Box
                  sx={{
                    px: 2,
                    pt: 1.25,
                    pb: 0.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography variant="caption" sx={{ color: "#6b7280", flex: 1, minWidth: 220 }}>
                    {current.caption}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Search pharmacy…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: 240, "& .MuiInputBase-input": { fontSize: "0.85rem" } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Tooltip arrow title="Manage the test / internal pharmacies hidden from Check-Ins">
                    <Box
                      component="button"
                      onClick={() => {
                        setExclusionPreset(null);
                        setExclusionsOpen(true);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.25,
                        py: 0.6,
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "#6b7280",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        background: "#fff",
                        cursor: "pointer",
                        "&:hover": { background: "#f3f4f6", color: "#374151" },
                      }}
                    >
                      <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} />
                      Ignored
                    </Box>
                  </Tooltip>
                  {current.worklist && (
                    <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Checkbox
                          size="small"
                          checked={showScheduled}
                          onChange={(e) => setShowScheduled(e.target.checked)}
                          sx={{ color: accent, "&.Mui-checked": { color: accent } }}
                        />
                      }
                      label={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "#374151" }}>
                            Show scheduled & stopped
                          </Typography>
                          <Tooltip
                            arrow
                            title="By default this list only shows pharmacies that are due — no re-check date, or a re-check date that has passed. Tick to also see the ones booked for a later date and the ones we've stopped calling."
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
                          </Tooltip>
                        </Box>
                      }
                    />
                  )}
                </Box>
                {tab === 3 && mediDropTrialError ? (
                  <Box sx={{ p: 2 }}>
                    <Alert severity="warning">{mediDropTrialError}</Alert>
                  </Box>
                ) : (
                  <div style={{ height: "calc(100vh - 330px)", minHeight: 520, width: "100%", padding: "4px 8px 12px" }}>
                    <DataGrid
                      rows={displayRows}
                      columns={current.columns}
                      loading={tab === 3 ? mediDropTrialLoading : loading}
                      pageSizeOptions={[25, 50, 100]}
                      initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                      disableRowSelectionOnClick
                      getRowClassName={(p) =>
                        [
                          p.row.extremelyAtRisk ? "checkins-extreme-row" : "",
                          p.row.stopCheckIns ? "checkins-stopped-row" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")
                      }
                      sx={{
                        ...(current.app === "medidrop" ? mediDropDataGridSx : qudaAdminDataGridSx),
                        "& .MuiDataGrid-cell": { alignItems: "center", display: "flex" },
                        "& .checkins-extreme-row": {
                          backgroundColor: "#fee2e2 !important",
                          "&:hover": { backgroundColor: "#fecaca !important" },
                        },
                        "& .checkins-stopped-row": {
                          opacity: 0.55,
                        },
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <CheckInModal
        open={!!checkInRow}
        onClose={() => setCheckInRow(null)}
        chemistUUID={checkInRow?.chemistUUID}
        chemistName={checkInRow?.chemistName}
        app={checkInRow?.app || "quda"}
        nextCheckInDate={checkInRow?.nextCheckInDate}
        stopCheckIns={checkInRow?.stopCheckIns}
        lastCheckInDate={checkInRow?.lastCheckInDate}
        onSaved={handleCheckInSaved}
      />

      <CheckInExclusionsModal
        open={exclusionsOpen}
        onClose={() => {
          setExclusionsOpen(false);
          setExclusionPreset(null);
        }}
        presetChemist={exclusionPreset}
        // Hiding or restoring a pharmacy changes every table and every count.
        onChanged={handleCheckInSaved}
      />
    </div>
  );
};

export default CheckInsPage;
